import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ConnectedUser } from 'src/app/shared/connected-user';
import { UserSessionService } from 'src/app/user-session.service';
import { AuthService, JwtResponse } from '../auth.service';
import { BusinessAccountService } from '../business-account.service';

@Injectable({ providedIn: 'root' })
export class BalanceSyncService {
  private syncInFlight$: Observable<ConnectedUser | null> | null = null;

  constructor(
    private authService: AuthService,
    private businessAccountService: BusinessAccountService,
    private userSessionService: UserSessionService
  ) { }

  syncNow(): Observable<ConnectedUser | null> {
    if (this.syncInFlight$) {
      return this.syncInFlight$;
    }

    const currentUser = this.userSessionService.getUser();
    const refreshToken = localStorage.getItem('refreshToken');

    if (!currentUser?.username || !refreshToken) {
      return of(currentUser ?? null);
    }

    this.syncInFlight$ = this.authService.refresh(refreshToken).pipe(
      take(1),
      switchMap((response: JwtResponse) => {
        this.authService.saveConnectedUser({
          ...response,
          businessAccounts: response.businessAccounts ?? currentUser.businessAccounts ?? []
        });

        const refreshedUser = this.userSessionService.getUser();
        const username = refreshedUser?.username ?? currentUser.username;
        if (!username) {
          return of(refreshedUser ?? null);
        }

        if (!this.shouldReloadBusinessAccounts(refreshedUser ?? currentUser)) {
          return of(refreshedUser ?? currentUser);
        }

        return this.businessAccountService.getAllUtilisateurBaccount(username).pipe(
          take(1),
          map((accounts: any) => this.persistBusinessAccounts(Array.isArray(accounts) ? accounts : [])),
          catchError(() => of(this.userSessionService.getUser()))
        );
      }),
      catchError(() => of(this.userSessionService.getUser())),
      finalize(() => {
        this.syncInFlight$ = null;
      }),
      shareReplay(1)
    );

    return this.syncInFlight$;
  }

  private persistBusinessAccounts(accounts: any[]): ConnectedUser | null {
    const current = this.userSessionService.getUser();
    if (!current) {
      return null;
    }

    this.userSessionService.setUser({
      ...current,
      businessAccounts: accounts
    });

    return this.userSessionService.getUser();
  }

  private shouldReloadBusinessAccounts(user: ConnectedUser | null): boolean {
    if (!user?.roles || user.roles.length === 0) {
      return false;
    }

    const normalizedRoles = user.roles
      .map(role => (role || '').toUpperCase().replace(/^ROLE_/, ''))
      .filter(Boolean);

    return normalizedRoles.includes('USER')
      || normalizedRoles.includes('ADMIN')
      || normalizedRoles.includes('SUPER_ADMIN')
      || normalizedRoles.includes('VENDOR');
  }
}
